import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LOAN_STORAGE_KEYS } from '@ld.consumer-portal/models';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

export const CIAM_ACCESS_TOKEN_KEY = 'ciam_access_token';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly B2C_TOKEN_KEY = 'b2c_token';
  private readonly B2C_ID_TOKEN_KEY = 'b2c_id_token';
  private readonly TOKEN_EXPIRY_KEY = 'token_expiry';

  private readonly EPS_TOKEN_KEY = 'eps_token';

  private readonly USER_ID_KEY = 'user_id';
  private readonly EXTERNAL_USER_ID_KEY = 'external_user_id';
  private readonly USER_NAME_KEY = 'user_name';
  private readonly USER_EMAIL_KEY = 'user_email';
  private readonly USER_PASSWORD_KEY = 'user_password';

  private readonly PENDING_B2C_TOKEN_KEY = 'pending_b2c_token';
  private readonly PENDING_B2C_ID_TOKEN_KEY = 'pending_b2c_id_token';
  private readonly PENDING_USER_ID_KEY = 'pending_user_id';
  private readonly PENDING_USER_NAME_KEY = 'pending_user_name';
  private readonly PENDING_USER_EMAIL_KEY = 'pending_user_email';

  private readonly CP_TOKEN_KEY = 'cp_token';
  private readonly CP_LOAN_ID_KEY = 'cp_loan_id';
  private readonly CP_BORROWER_ID_KEY = 'cp_borrower_id';
  private readonly CP_USER_GUID_KEY = 'cp_user_guid';
  private readonly CP_VISITOR_ID_KEY = 'cp_visitor_id';
  private readonly CP_MLA_DOMAIN_LINK_KEY = 'cp_mla_domain_link';
  private readonly CP_TOKEN_EXPIRY_KEY = 'cp_token_expiry';

  readonly isAuthenticated = signal(this.hasToken());

  readonly userName = signal<string | null>(localStorage.getItem(this.USER_NAME_KEY));

  private crossTabSyncInitialized = false;

  private hasToken(): boolean {
    return !!localStorage.getItem(this.B2C_TOKEN_KEY);
  }

  verifyAuthState(): boolean {
    const hasToken = this.hasToken();
    if (this.isAuthenticated() !== hasToken) {
      this.isAuthenticated.set(hasToken);
    }
    return hasToken;
  }

  getToken(): string | null {
    return localStorage.getItem(this.B2C_TOKEN_KEY);
  }

  getUserId(): string | null {
    return localStorage.getItem(this.USER_ID_KEY);
  }

  getUserName(): string | null {
    return localStorage.getItem(this.USER_NAME_KEY);
  }

  getUserEmail(): string | null {
    return localStorage.getItem(this.USER_EMAIL_KEY);
  }

  extractNamesFromB2cToken(returnNameIfEmpty?: boolean): { firstName: string; lastName: string } {
    const idToken = localStorage.getItem(this.B2C_ID_TOKEN_KEY);
    if (!idToken) return { firstName: '', lastName: '' };
    try {
      const parts = idToken.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        const result = {
          firstName: payload.extension_FirstName || payload.given_name || '',
          lastName: payload.extension_LastName || payload.family_name || '',
        };
        if (!result.firstName && !result.lastName && payload.name && returnNameIfEmpty === true) {
          // tokens currently only exposes 'name' property which is the full first+last name
          result.firstName = payload.name;
        }
        return result;
      }
    } catch {
      // Token is not a decodable JWT
    }
    return { firstName: '', lastName: '' };
  }

  getExternalUserId(): string | null {
    const stored = localStorage.getItem(this.EXTERNAL_USER_ID_KEY);
    if (stored) {
      return stored;
    }
    // Fallback: extract oid/sub from the B2C ID token
    const idToken = localStorage.getItem(this.B2C_ID_TOKEN_KEY);
    if (idToken) {
      try {
        const parts = idToken.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          return payload.oid || payload.sub || null;
        }
      } catch {
        // Token is not a decodable JWT
      }
    }
    return null;
  }

  setExternalUserId(id: string): void {
    localStorage.setItem(this.EXTERNAL_USER_ID_KEY, id);
  }

  getB2cIdToken(): string | null {
    return localStorage.getItem(this.B2C_ID_TOKEN_KEY);
  }

  setToken(token: string): void {
    localStorage.setItem(this.B2C_TOKEN_KEY, token);
    this.isAuthenticated.set(true);
    this.extractAndStoreTokenExpiry(token);
  }

  setB2cIdToken(token: string): void {
    localStorage.setItem(this.B2C_ID_TOKEN_KEY, token);
  }

  setCiamAccessToken(token: string): void {
    localStorage.setItem(CIAM_ACCESS_TOKEN_KEY, token);
  }

  getCiamAccessToken(): string | null {
    return localStorage.getItem(CIAM_ACCESS_TOKEN_KEY);
  }

  setUserPassword(password: string): void {
    sessionStorage.setItem(this.USER_PASSWORD_KEY, password);
  }

  getUserPassword(): string | null {
    return sessionStorage.getItem(this.USER_PASSWORD_KEY);
  }

  clearUserPassword(): void {
    sessionStorage.removeItem(this.USER_PASSWORD_KEY);
  }

  setPendingTokens(accessToken: string, idToken: string): void {
    sessionStorage.setItem(this.PENDING_B2C_TOKEN_KEY, accessToken);
    sessionStorage.setItem(this.PENDING_B2C_ID_TOKEN_KEY, idToken);
  }

  setPendingUserInfo(userId: string, firstName?: string, lastName?: string, email?: string): void {
    sessionStorage.setItem(this.PENDING_USER_ID_KEY, userId);
    if (firstName || lastName) {
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      sessionStorage.setItem(this.PENDING_USER_NAME_KEY, fullName);
    }
    if (email) {
      sessionStorage.setItem(this.PENDING_USER_EMAIL_KEY, email);
    }
  }

  getPendingToken(): string | null {
    return sessionStorage.getItem(this.PENDING_B2C_TOKEN_KEY);
  }

  getPendingB2cIdToken(): string | null {
    return sessionStorage.getItem(this.PENDING_B2C_ID_TOKEN_KEY);
  }

  getPendingUserId(): string | null {
    return sessionStorage.getItem(this.PENDING_USER_ID_KEY);
  }

  getPendingUserEmail(): string | null {
    return sessionStorage.getItem(this.PENDING_USER_EMAIL_KEY);
  }

  commitPendingTokens(): void {
    const accessToken = sessionStorage.getItem(this.PENDING_B2C_TOKEN_KEY);
    const idToken = sessionStorage.getItem(this.PENDING_B2C_ID_TOKEN_KEY);
    const userId = sessionStorage.getItem(this.PENDING_USER_ID_KEY);
    const userName = sessionStorage.getItem(this.PENDING_USER_NAME_KEY);
    const userEmail = sessionStorage.getItem(this.PENDING_USER_EMAIL_KEY);

    if (accessToken) {
      this.setToken(accessToken);
      this.extractAndStoreTokenExpiry(accessToken);
    }
    if (idToken) {
      this.setB2cIdToken(idToken);
    }
    if (userId) {
      this.setUserInfo(userId, undefined, undefined, userEmail || undefined);
      if (userName) {
        localStorage.setItem(this.USER_NAME_KEY, userName);
        this.userName.set(userName);
      }
    }

    this.clearPendingTokens();
  }

  clearPendingTokens(): void {
    sessionStorage.removeItem(this.PENDING_B2C_TOKEN_KEY);
    sessionStorage.removeItem(this.PENDING_B2C_ID_TOKEN_KEY);
    sessionStorage.removeItem(this.PENDING_USER_ID_KEY);
    sessionStorage.removeItem(this.PENDING_USER_NAME_KEY);
    sessionStorage.removeItem(this.PENDING_USER_EMAIL_KEY);
  }

  hasPendingTokens(): boolean {
    return !!sessionStorage.getItem(this.PENDING_B2C_TOKEN_KEY);
  }

  setUserInfo(userId: string, firstName?: string, lastName?: string, email?: string): void {
    localStorage.setItem(this.USER_ID_KEY, userId);
    if (firstName || lastName) {
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      localStorage.setItem(this.USER_NAME_KEY, fullName);
      this.userName.set(fullName);
    }
    if (email) {
      localStorage.setItem(this.USER_EMAIL_KEY, email);
    }
  }

  clearToken(): void {
    localStorage.removeItem(this.B2C_TOKEN_KEY);
    localStorage.removeItem(this.B2C_ID_TOKEN_KEY);
    localStorage.removeItem(CIAM_ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.USER_ID_KEY);
    localStorage.removeItem(this.USER_NAME_KEY);
    localStorage.removeItem(this.USER_EMAIL_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.EXTERNAL_USER_ID_KEY);
    sessionStorage.removeItem(this.USER_PASSWORD_KEY);

    localStorage.removeItem(LOAN_STORAGE_KEYS.SELECTED_LOAN);
    localStorage.removeItem(LOAN_STORAGE_KEYS.SELECTED_LOAN_DETAILS);
    localStorage.removeItem(LOAN_STORAGE_KEYS.IS_HELOC);
    localStorage.removeItem(LOAN_STORAGE_KEYS.HELOC_LOAN_NUMBER);
    localStorage.removeItem(LOAN_STORAGE_KEYS.ENCRYPTION_VALUE);
    localStorage.removeItem(LOAN_STORAGE_KEYS.PIIT_DISMISSED_LOANS);
    localStorage.removeItem(LOAN_STORAGE_KEYS.PIIT_CURRENT_LOAN);

    localStorage.removeItem('native_ciam_refresh_token');
    // Keep native_ciam_auth_options for biometric silent refresh after app restart

    this.isAuthenticated.set(false);
    this.userName.set(null);
  }

  async logout(): Promise<void> {
    await this.router.navigate(['/sign-out']);
  }

  async logoutLocal(): Promise<void> {
    await this.router.navigate(['/sign-out'], { replaceUrl: true });
  }

  initCrossTabSync(): void {
    if (this.crossTabSyncInitialized) {
      return;
    }

    this.crossTabSyncInitialized = true;

    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        filter((event) => event.key === this.B2C_TOKEN_KEY && !localStorage.getItem(this.B2C_TOKEN_KEY)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.isAuthenticated.set(false);
        this.router.navigate(['/login'], { replaceUrl: true });
      });
  }

  setTokenExpiry(expiresAt: number): void {
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiresAt.toString());
  }

  getTokenExpiry(): number | null {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (expiry) {
      return parseInt(expiry, 10);
    }
    const token = localStorage.getItem(this.B2C_TOKEN_KEY);
    if (token) {
      this.extractAndStoreTokenExpiry(token);
      const recovered = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      if (recovered) {
        return parseInt(recovered, 10);
      }
    }
    return null;
  }

  isTokenExpiringSoon(bufferSeconds = 300): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) {
      return false;
    }
    const now = Math.floor(Date.now() / 1000);
    return expiry - now < bufferSeconds;
  }

  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) {
      return true;
    }
    const now = Math.floor(Date.now() / 1000);
    return expiry < now;
  }

  getCpToken(): string | null {
    return localStorage.getItem(this.CP_TOKEN_KEY);
  }

  setCpToken(token: string): void {
    localStorage.setItem(this.CP_TOKEN_KEY, token);
  }

  getCpLoanId(): string | null {
    return localStorage.getItem(this.CP_LOAN_ID_KEY);
  }

  getCpBorrowerId(): string | null {
    return localStorage.getItem(this.CP_BORROWER_ID_KEY);
  }

  getCpUserGuid(): string | null {
    return localStorage.getItem(this.CP_USER_GUID_KEY);
  }

  getCpVisitorId(): string | null {
    return localStorage.getItem(this.CP_VISITOR_ID_KEY);
  }

  getCpMlaDomainLink(): string | null {
    return localStorage.getItem(this.CP_MLA_DOMAIN_LINK_KEY);
  }

  setCpContext(context: {
    authToken: string;
    loanId: string;
    borrowerId: string;
    userGuid: string;
    expiresInSeconds: number;
    visitorId?: string;
    mlaDomainLink?: string;
    email?: string;
  }): void {
    localStorage.setItem(this.CP_TOKEN_KEY, context.authToken);
    localStorage.setItem(this.CP_LOAN_ID_KEY, context.loanId);
    localStorage.setItem(this.CP_BORROWER_ID_KEY, context.borrowerId);
    localStorage.setItem(this.CP_USER_GUID_KEY, context.userGuid);

    if (context.visitorId) {
      localStorage.setItem(this.CP_VISITOR_ID_KEY, context.visitorId);
    }
    if (context.mlaDomainLink) {
      localStorage.setItem(this.CP_MLA_DOMAIN_LINK_KEY, context.mlaDomainLink);
    }
    if (context.email) {
      localStorage.setItem(this.USER_EMAIL_KEY, context.email);
    }

    const expiresAt = Math.floor(Date.now() / 1000) + context.expiresInSeconds;
    localStorage.setItem(this.CP_TOKEN_EXPIRY_KEY, expiresAt.toString());

    this.setCpAuthCookie(context.authToken);
  }

  setCpAuthCookie(authToken: string): void {
    document.cookie = `token=${authToken}; path=/; Secure; SameSite=None`;
  }

  clearCpAuthCookie(): void {
    document.cookie = 'token=; path=/; Secure; SameSite=None; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }

  clearCpContext(): void {
    localStorage.removeItem(this.CP_TOKEN_KEY);
    localStorage.removeItem(this.CP_LOAN_ID_KEY);
    localStorage.removeItem(this.CP_BORROWER_ID_KEY);
    localStorage.removeItem(this.CP_USER_GUID_KEY);
    localStorage.removeItem(this.CP_VISITOR_ID_KEY);
    localStorage.removeItem(this.CP_MLA_DOMAIN_LINK_KEY);
    localStorage.removeItem(this.CP_TOKEN_EXPIRY_KEY);
    this.clearCpAuthCookie();
  }

  getCpTokenExpiry(): number | null {
    const expiry = localStorage.getItem(this.CP_TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : null;
  }

  isCpTokenExpiringSoon(bufferSeconds = 300): boolean {
    const expiry = this.getCpTokenExpiry();
    if (!expiry) {
      return true;
    }
    const now = Math.floor(Date.now() / 1000);
    return expiry - now < bufferSeconds;
  }

  isCpTokenExpired(): boolean {
    const expiry = this.getCpTokenExpiry();
    if (!expiry) {
      return true;
    }
    const now = Math.floor(Date.now() / 1000);
    return expiry < now;
  }

  hasCpContext(): boolean {
    return !!(this.getCpToken() && this.getCpLoanId() && this.getCpBorrowerId());
  }

  clearAllTokens(): void {
    this.clearToken();
    this.clearCpContext();
  }

  private extractAndStoreTokenExpiry(token: string): void {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return;
      const payload = JSON.parse(atob(parts[1]));
      if (typeof payload.exp === 'number') {
        this.setTokenExpiry(payload.exp);
      }
    } catch {
      // Token is not a decodable JWT; skip expiry extraction
    }
  }
}
